import { useNavigate } from "react-router-dom";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useCompanies } from "@/hooks/use-companies";
import {
  TenantLogo,
  TenantStatusBadge,
  TenantCounts,
  useAllTenantAgents,
  useOpenTickets,
} from "@/components/admin/tenant-summary";
import { Building2, ArrowRight } from "lucide-react";

export function AdminDashboard() {
  const navigate = useNavigate();
  const { data: companies = [] } = useCompanies();

  const { data: allAgents = [] } = useAllTenantAgents(companies);
  const openTickets = useOpenTickets();

  const activeAgents = allAgents.filter((a) => a.status === "active" || a.status === "running");

  return (
    <CenterPanel title="Platform Dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Total Tenants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{companies.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Active Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{activeAgents.length}</p>
            <p className="text-xs text-text-muted">{allAgents.length} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Total Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{allAgents.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tenant list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 size={18} />
            Tenants
          </CardTitle>
        </CardHeader>
        <CardContent>
          {companies.length === 0 ? (
            <p className="text-sm text-text-muted">No tenants yet.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {companies.map((company) => {
                const agentCount = allAgents.filter((a) => a.companyId === company.id).length;
                const ticketCount = openTickets.filter((t) => t.companyId === company.id).length;
                return (
                  <div
                    key={company.id}
                    className="flex items-center justify-between rounded px-3 py-2.5 cursor-pointer hover:bg-surface-hover"
                    onClick={() => navigate(`/tenants/${company.id}/dashboard`)}
                  >
                    <div className="flex items-center gap-3">
                      <TenantLogo company={company} />
                      <div>
                        <p className="text-sm font-medium text-text-primary">{company.name}</p>
                        <TenantCounts agents={agentCount} openTickets={ticketCount} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TenantStatusBadge status={company.status} />
                      <ArrowRight size={16} className="text-text-muted" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </CenterPanel>
  );
}
